import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { documentsAPI } from '../services/api';
import Modal from '../components/common/Modal';
import { Loading, EmptyState } from '../components/common';
import { formatDate, formatFileSize } from '../utils/formatters';
import { DOCUMENT_TYPES } from '../utils/constants';
import toast from 'react-hot-toast';
import { DocumentTextIcon, PlusIcon, ArrowDownTrayIcon, EyeIcon, TrashIcon } from '@heroicons/react/24/outline';

const Documents = () => {
  const { user } = useSelector((state) => state.auth);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [typeFilter, setTypeFilter] = useState('');
  const [formData, setFormData] = useState({ title: '', document_type: 'normative', content: '', is_published: true });
  const [file, setFile] = useState(null);

  const fetchDocuments = async () => {
    try {
      const params = typeFilter ? { document_type: typeFilter } : {};
      const response = await documentsAPI.getAll(params);
      setDocuments(response.data.results || response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDocuments(); }, [typeFilter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = new FormData();
      Object.entries(formData).forEach(([k, v]) => data.append(k, v));
      if (file) data.append('file', file);
      
      await documentsAPI.create(data);
      toast.success('Hujjat yuklandi!');
      setShowModal(false);
      setFormData({ title: '', document_type: 'normative', content: '', is_published: true });
      setFile(null);
      fetchDocuments();
    } catch (error) {
      toast.error('Xatolik yuz berdi');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('O\'chirishni xohlaysizmi?')) return;
    try {
      await documentsAPI.delete(id);
      toast.success('O\'chirildi!');
      fetchDocuments();
    } catch (error) {
      toast.error('Xatolik yuz berdi');
    }
  };

  const isAdmin = user?.role === 'admin';

  if (loading) return <Loading />;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Hujjatlar</h1>
          <p className="text-gray-500">Me'yoriy hujjatlar va yangiliklar</p>
        </div>
        {isAdmin && (
          <button onClick={() => setShowModal(true)} className="btn-primary">
            <PlusIcon className="w-5 h-5" /> Yangi hujjat
          </button>
        )}
      </div>

      <div className="card">
        <div className="flex gap-2">
          <button onClick={() => setTypeFilter('')} className={`px-4 py-2 rounded-lg ${!typeFilter ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}>Barchasi</button>
          {Object.entries(DOCUMENT_TYPES).map(([k, v]) => (
            <button key={k} onClick={() => setTypeFilter(k)} className={`px-4 py-2 rounded-lg ${typeFilter === k ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}>{v}</button>
          ))}
        </div>
      </div>

      {documents.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {documents.map((doc) => (
            <div key={doc.id} className="card">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <DocumentTextIcon className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-800 truncate">{doc.title}</h3>
                  <p className="text-sm text-gray-500">{DOCUMENT_TYPES[doc.document_type]}</p>
                  <p className="text-xs text-gray-400 mt-1">{formatDate(doc.created_at)}</p>
                </div>
              </div>
              {doc.content && <p className="text-sm text-gray-600 mt-4 line-clamp-2">{doc.content}</p>}
              <div className="flex gap-2 mt-4 pt-4 border-t">
                {doc.file && (
                  <a href={doc.file} target="_blank" rel="noopener noreferrer" className="btn-ghost btn-sm">
                    <ArrowDownTrayIcon className="w-4 h-4" /> Yuklab olish
                  </a>
                )}
                {isAdmin && (
                  <button onClick={() => handleDelete(doc.id)} className="btn-ghost btn-sm text-red-600">
                    <TrashIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState icon={DocumentTextIcon} title="Hujjatlar topilmadi" />
      )}

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Yangi hujjat" size="lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="input-group">
            <label className="label">Sarlavha *</label>
            <input type="text" className="input" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required />
          </div>
          <div className="input-group">
            <label className="label">Turi</label>
            <select className="select" value={formData.document_type} onChange={(e) => setFormData({ ...formData, document_type: e.target.value })}>
              {Object.entries(DOCUMENT_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div className="input-group">
            <label className="label">Matn</label>
            <textarea className="textarea" value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })} />
          </div>
          <div className="input-group">
            <label className="label">Fayl</label>
            <input type="file" className="input" onChange={(e) => setFile(e.target.files[0])} accept=".pdf,.docx,.xlsx" />
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">Bekor qilish</button>
            <button type="submit" className="btn-primary">Yuklash</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Documents;
